import { z } from 'zod';

// =============================================================================
//  CRM Activity schemas — Story 11.1 (fundação) + 11.8 (CRUD + minhas tarefas)
//  Fonte única back+front (arquitetura 08-fase3-crm-architecture.md § III.5, FR-58).
//  Alvo polimórfico: EXATAMENTE 1 de (lead_id, deal_id, client_id) — espelha o
//  CHECK do banco (crm_activity_single_target). due_date só faz sentido em tarefa.
//  Tipos `mudanca_estagio`/`proposta_enviada` são SYSTEM-ONLY (gerados por código
//  interno via createSystem); o endpoint público os rejeita (AC2).
// =============================================================================

export const crmActivityTypeSchema = z.enum([
  'ligacao',
  'mensagem',
  'reuniao',
  'email',
  'nota',
  'mudanca_estagio',
  'proposta_enviada',
  'tarefa',
]);
export type CrmActivityTypeSchema = z.infer<typeof crmActivityTypeSchema>;

/** Tipos gerados apenas pelo sistema (não criáveis via endpoint público — AC2). */
export const CRM_ACTIVITY_SYSTEM_ONLY_TYPES = ['mudanca_estagio', 'proposta_enviada'] as const;

const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const ymdSchema = z.string().regex(YMD_REGEX, 'Data deve estar no formato YYYY-MM-DD');

// -----------------------------------------------------------------------------
// CREATE — exige exatamente 1 alvo (espelha o CHECK polimórfico do DB).
// `type=tarefa` exige `due_date`. Tipos system-only são rejeitados (AC1/AC2).
// `occurred_at` opcional (default now() no backend) — permite atividade retroativa.
// `author_user_id`/`is_system` NUNCA vêm do cliente (definidos no backend).
// -----------------------------------------------------------------------------

export const createCrmActivitySchema = z
  .object({
    type: crmActivityTypeSchema,
    subject: z
      .string()
      .trim()
      .min(1, 'Assunto obrigatório.')
      .max(200, 'Assunto deve ter até 200 caracteres.'),
    description: z.string().max(5000).optional().nullable(),
    lead_id: z.string().uuid('lead_id inválido.').optional().nullable(),
    deal_id: z.string().uuid('deal_id inválido.').optional().nullable(),
    client_id: z.string().uuid('client_id inválido.').optional().nullable(),
    due_date: ymdSchema.optional().nullable(),
    occurred_at: z
      .string()
      .datetime({ offset: true, message: 'occurred_at deve ser ISO 8601.' })
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    // Alvo polimórfico: exatamente 1 (espelha o CHECK do DB).
    const targets = [data.lead_id, data.deal_id, data.client_id].filter(
      (v) => v !== undefined && v !== null,
    );
    if (targets.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lead_id'],
        message: 'Informe exatamente um alvo: lead_id, deal_id ou client_id.',
      });
    }

    // Tipos system-only não podem ser criados manualmente (AC2).
    if (CRM_ACTIVITY_SYSTEM_ONLY_TYPES.includes(data.type as never)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type'],
        message: 'Este tipo de atividade é gerado automaticamente pelo sistema.',
      });
    }

    // type=tarefa exige due_date (FR-58.2 / AC2).
    if (data.type === 'tarefa' && (data.due_date === undefined || data.due_date === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['due_date'],
        message: 'dueDate obrigatório para atividades do tipo tarefa.',
      });
    }
  });
export type CreateCrmActivityDto = z.output<typeof createCrmActivitySchema>;
export type CreateCrmActivityInput = z.input<typeof createCrmActivitySchema>;

// -----------------------------------------------------------------------------
// UPDATE — alvo e tipo são imutáveis; aqui só o conteúdo, agendamento e o toggle
// de tarefa concluída. `done_at` é derivado pelo backend (não aceito do cliente).
// -----------------------------------------------------------------------------

export const updateCrmActivitySchema = z
  .object({
    subject: z.string().trim().min(1, 'Assunto obrigatório.').max(200).optional(),
    description: z.string().max(5000).optional().nullable(),
    due_date: ymdSchema.optional().nullable(),
    occurred_at: z
      .string()
      .datetime({ offset: true, message: 'occurred_at deve ser ISO 8601.' })
      .optional(),
    done: z.boolean().optional(),
  })
  .strict();
export type UpdateCrmActivityDto = z.output<typeof updateCrmActivitySchema>;
export type UpdateCrmActivityInput = z.input<typeof updateCrmActivitySchema>;

// -----------------------------------------------------------------------------
// QUERY — por alvo OU "minhas tarefas" (FR-58.5); cursor pagination (DR-032).
// `my_tasks=true` → tarefas do usuário logado (author resolvido no backend).
// `due_before` filtra tarefas com due_date <= X (Atrasadas/Hoje/Esta semana).
// -----------------------------------------------------------------------------

const booleanQuerySchema = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((v) => v === true || v === 'true' || v === '1');

export const queryCrmActivitiesSchema = z.object({
  lead_id: z.string().uuid('lead_id inválido.').optional(),
  deal_id: z.string().uuid('deal_id inválido.').optional(),
  client_id: z.string().uuid('client_id inválido.').optional(),
  type: crmActivityTypeSchema.optional(),
  author_user_id: z.string().uuid('author_user_id inválido.').optional(),
  done: booleanQuerySchema.optional(),
  due_before: ymdSchema.optional(),
  my_tasks: booleanQuerySchema.optional(),
  cursor: z.string().uuid('cursor inválido.').optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QueryCrmActivitiesDto = z.infer<typeof queryCrmActivitiesSchema>;
